import type {
    WarningQueryRequest,
    WarningScoreRequest,
    ThreatScorePeriod,
} from '../../../api/warning-and-scoring/types/warning-and-scoring.types'
import type {GuidType} from '../../../values/generic-type-definitions'

export const warningKeys = {
    all: ['warnings'] as const,
    list: (filters: WarningQueryRequest) => [...warningKeys.all, filters] as const,
    score: (filters: WarningScoreRequest) => ['warning-score', filters] as const,
    config: () => ['warning-config'] as const,
    listById: (warningId: string, filters: WarningQueryRequest) =>
        ['warnings', 'by-id', warningId, filters] as const,
    countByLocation: ['warning-count-by-location'] as const,
}

// Threat score keys (CDS-6008). Kept separate from warningKeys because these
// come from a different WaS resource (/scores) with different cache semantics.
export const threatScoreKeys = {
    all: ['threat-scores'] as const,
    latestFleet: () => [...threatScoreKeys.all, 'latest-fleet'] as const,
    fleetTrend: (period: ThreatScorePeriod) =>
        [...threatScoreKeys.all, 'fleet-trend', period] as const,
    latestLocations: () => [...threatScoreKeys.all, 'latest-locations'] as const,
    locationTrend: (period: ThreatScorePeriod, location: GuidType) =>
        [...threatScoreKeys.all, 'location-trend', period, location] as const,
    locationTagTrend: (period: ThreatScorePeriod, tag: string) =>
        [...threatScoreKeys.all, 'location-tag-trend', period, tag] as const,
    scoresByTags: (period: ThreatScorePeriod, tags: GuidType[]) =>
        [...threatScoreKeys.all, 'scores-by-tags', period, tags] as const,
}

/**
 * Key for the non-WaS half of the Compare Fleets widget. Kept here so the whole
 * tag-summary composition is discoverable in one place.
 *
 * This keys on the selected range rather than on the resolved window start. The
 * window start moves with the clock, so putting it in the key would mint a new
 * query on every tick and refetch out of step with `refetchInterval`. The
 * queryFn resolves it at call time instead.
 */
export const tagSummaryKeys = {
    all: ['tag-summary'] as const,
    incidentCount: (tag: GuidType, timeRange: string, locations: readonly GuidType[]) =>
        [...tagSummaryKeys.all, 'incident-count', tag, timeRange, locations] as const,
}
